import styled from 'styled-components';
import * as MdIcons from 'react-icons/md';
import { useModalProvider } from "../../../Providers/ModalProvider";
import ModalHeader from "../../../Components/ModalHeader/ModalHeader";
import AccountSettingsModal from '../../../Components/PageHeader/AccountSettingsModal';
import { useState, useEffect } from 'react';
import ConfirmDeleteAccount from './ConfirmDeleteAccount.js';
import ModalScrollbar from '../../../Components/ModalScrollbar/ModalScrollbar';
import { MobergButton, MobergButtonVariant, MobergButtonShape, MobergTheme, MobergFontSize } from '../../../Moberg';
import { MobergFontFamily } from '../../../Components/MobergFont/MobergFont';

const DeleteAccount = (props) => {
    const { openNext, createModal } = useModalProvider()
    const [currentPassword, setCurrentPassword] = useState("")
    const [confirmCurrentPassword, setConfirmCurrentPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [showErrorMessage, setShowErrorMessage] = useState(false)
    const [disableDeleteAccountButton, setDisableDeleteAccountButton] = useState(true)

    useEffect(() => {
        if (!currentPassword && !confirmCurrentPassword) {
            setDisableDeleteAccountButton(true)
            setShowErrorMessage(false)
        } else if (!currentPassword) {
            setDisableDeleteAccountButton(true)
            setErrorMessage("You must enter your password.")
            setShowErrorMessage(true)
        } else if (!confirmCurrentPassword) {
            setDisableDeleteAccountButton(true)
            setErrorMessage("You must confirm your password.")
            setShowErrorMessage(true)
        } else if (currentPassword !== confirmCurrentPassword) {
            setDisableDeleteAccountButton(true)
            setErrorMessage("Passwords do not match.")
            setShowErrorMessage(true)
        } else {
            setShowErrorMessage(false)
            setDisableDeleteAccountButton(false)
        }
    }, [currentPassword, confirmCurrentPassword])

    function renderAccountSettingsModal() {
        openNext(<AccountSettingsModal
            escClose={false}
            clickOutsideClose={false}
        />)
    }

    function renderConfirmDeleteAccountModal() {
        createModal(<ConfirmDeleteAccount
            escClose={false}
            clickOutsideClose={false}
            password={confirmCurrentPassword}
        />)
    }


    return (
        <ModalContainer>
            <ModalHeader headerText='Account Settings' />
            <ModalScrollbar>
                <ContentContainer>
                    <BackButton onClick={renderAccountSettingsModal}>
                        <MdIcons.MdArrowBack size={20} />
                        <span>Back</span>
                    </BackButton>

                    <PageTitle>Delete Account</PageTitle>

                    <WarningCard>
                        <MdIcons.MdWarning size={20} style={{ color: '#e64e58', flexShrink: 0 }} />
                        <WarningText>
                            <strong>This action cannot be undone.</strong> Deleting your account will permanently remove all your data, settings, and access to the platform.
                        </WarningText>
                    </WarningCard>

                    <FormCard>
                        <FormDescription>
                            To confirm account deletion, please enter your password below:
                        </FormDescription>

                        <FormGroup>
                            <Label>Password</Label>
                            <Input 
                                placeholder="Password" 
                                onChange={(e) => setCurrentPassword(e.target.value)} 
                                type={showPassword ? "text" : ((String(currentPassword).length === 0) ? "text" : "password")} 
                                value={currentPassword || ""} 
                                onCopy={(e) => e.preventDefault()} 
                                onPaste={(e) => e.preventDefault()} 
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Confirm Password</Label>
                            <Input 
                                placeholder="Password" 
                                onChange={(e) => setConfirmCurrentPassword(e.target.value)} 
                                type={showPassword ? "text" : ((String(confirmCurrentPassword).length === 0) ? "text" : "password")} 
                                value={confirmCurrentPassword || ""} 
                                onCopy={(e) => e.preventDefault()} 
                                onPaste={(e) => e.preventDefault()} 
                            />
                        </FormGroup>

                        <CheckboxWrapper>
                            <input 
                                id="showPasswordCheckbox" 
                                type="checkbox"
                                onChange={() => { setShowPassword(!showPassword) }}
                            />
                            <CheckboxLabel htmlFor="showPasswordCheckbox">Show Password</CheckboxLabel>
                        </CheckboxWrapper>

                        {showErrorMessage && (
                            <ErrorCard>
                                <MdIcons.MdWarning size={20} style={{ color: '#e64e58', marginRight: '8px' }} />
                                <ErrorText>{errorMessage}</ErrorText>
                            </ErrorCard>
                        )}

                        <ButtonWrapper>
                            <MobergButton
                                theme={MobergTheme.RED}
                                variant={MobergButtonVariant.FILLED}
                                onClick={renderConfirmDeleteAccountModal}
                                shape={MobergButtonShape.WIDE}
                                fontSize={MobergFontSize.LARGE}
                                disabled={disableDeleteAccountButton}
                            >
                                Delete Account
                            </MobergButton>
                        </ButtonWrapper>
                    </FormCard>
                </ContentContainer>
            </ModalScrollbar>
        </ModalContainer>
    )
}

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--color-surface-panel);
    width: 820px;
    max-height: 776px;
`;

const ContentContainer = styled.div`
    max-width: 500px;
    margin: 0 auto;
    padding: 24px 0;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    font-family: 'Source Sans Pro';
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    cursor: pointer;
    padding: 8px 12px;
    margin-bottom: 24px;
    border-radius: 6px;
    transition: background-color 0.2s ease;

    &:hover {
        background: var(--color-hover-neutral);
    }
`;

const PageTitle = styled.h1`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 24px;
    color: var(--color-text-primary);
    line-height: 150%;
    margin: 0 0 24px 0;
    text-align: center;
`;

const WarningCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(230, 78, 88, 0.1);
    border: 1px solid rgba(230, 78, 88, 0.3);
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 24px;
`;

const WarningText = styled.p`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: var(--text-secondary-color);
    line-height: 150%;
    margin: 0;

    strong {
        font-weight: 600;
        color: #e64e58;
    }
`;

const FormCard = styled.div`
    background: var(--color-surface-menu);
    border-radius: 8px;
    box-shadow: var(--modal-box-shadow);
    padding: 24px;
`;

const FormDescription = styled.p`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: var(--text-secondary-color);
    line-height: 150%;
    margin: 0 0 24px 0;
`;

const FormGroup = styled.div`
    margin-bottom: 24px;
`;

const Label = styled.label`
    display: block;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    color: var(--color-text-primary);
    line-height: 150%;
    margin-bottom: 8px;
`;

const Input = styled.input`
    width: 100%;
    background: var(--color-input-bg);
    border: 1px solid var(--color-border-default);
    box-sizing: border-box;
    border-radius: 6px;
    height: 48px;
    padding: 12px 16px;
    color: var(--color-text-primary);
    font-family: 'Source Sans Pro';
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
        outline: none;
        border-color: #e64e58;
    }

    &::placeholder {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 150%;
        color: var(--text-secondary-color);
    }
`;

const CheckboxWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        margin: 0;
    }
`;

const CheckboxLabel = styled.label`
    font-family: 'Source Sans Pro';
    font-size: 14px;
    color: var(--color-text-primary);
    margin-left: 8px;
    cursor: pointer;
`;

const ErrorCard = styled.div`
    display: flex;
    align-items: center;
    background: rgba(230, 78, 88, 0.1);
    border: 1px solid rgba(230, 78, 88, 0.3);
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 24px;
`;

const ErrorText = styled.p`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: #e64e58;
    line-height: 150%;
    margin: 0;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 24px;
`;

export default DeleteAccount;